// Einstellungen → Claude (E13 of APP-PARITY.md; board "Einstellungen: Claude", 22.10.2026): the key of the account
// (Dominik, 19.10.2026: "pro account ... bei uns gespeichert"): typed once, checked by the API with Anthropic and kept
// sealed in the cloud; afterwards "Schlüssel gespeichert" with its end and date, "Ersetzen" and "Löschen". Below it
// whether the app asks by itself after a sour or bitter rating (Dominik 22.10.2026, on by default), the model and the
// thoroughness: all of them follow the account. The grinder and what Claude knows about it live in Bezug → Bohnen &
// Mühle (GrinderInfo). The key field is no <form>: nothing can ever submit it to a URL. A key the first version left in
// this browser is offered once for the account.
import { useId, useState } from 'react';
import type { ClaudeEffort, ClaudeModel } from '@brewcore/app-contract';
import { failureText } from '../../ai/view.js';
import { dateText } from '../../core/index.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  Icon,
  Pending,
  Segmented,
  TextField,
  Toggle,
} from '../components/index.js';
import { useMachine } from '../machine-context.js';
import { AskSheet } from '../tabs/parts.js';
import { useAssistant } from './use-assistant.js';

export const KEY_CONSOLE_URL = 'https://console.anthropic.com/settings/keys';
const MODELS: { value: ClaudeModel; label: string }[] = [
  { value: 'claude-opus-5-5', label: 'Opus 5.5' },
  { value: 'claude-sonnet-5-5', label: 'Sonnet 5.5' },
];

export function ClaudeCard() {
  const { ctx } = useMachine();
  const { assistant, view } = useAssistant();
  const [key, setKey] = useState('');
  const [replacing, setReplacing] = useState(false);
  const [forgetting, setForgetting] = useState(false);
  const fieldId = useId();
  const titleId = useId();
  const autoId = useId();
  const account = view.account;
  const stored = account?.key ?? null;
  const info = (text: string, testId: string) => ({ text, label: ctx.tr('infoShow'), testId });
  const save = async () => {
    if (view.saving || !key.trim()) return;
    if (await assistant.saveKey(key)) {
      setKey('');
      setReplacing(false);
    }
  };
  const keyError = view.keyError
    ? view.keyError === 'claude_key_invalid'
      ? ctx.tr('aiKeyRefused')
      : failureText(ctx.lang, view.keyError)
    : undefined;
  const entry = (
    <>
      <TextField
        id={fieldId}
        type="password"
        label={ctx.tr('aiKeyLabel')}
        placeholder="sk-ant-…"
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        value={key}
        disabled={!view.loaded || view.saving}
        error={keyError}
        onChange={(e) => setKey(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save();
        }}
        data-testid="claude-key"
      />
      <div className="btn-row">
        {/* without a key typed it is a quiet locked button, not a dead orange-grey pill (UX line) */}
        <Button
          variant={key.trim() ? 'primary' : 'secondary'}
          busy={view.saving}
          disabled={!view.loaded || view.saving || !key.trim()}
          onClick={() => void save()}
          data-testid="claude-save"
        >
          {ctx.tr('aiKeySave')}
        </Button>
        {replacing && (
          <Button
            variant="secondary"
            disabled={view.saving}
            onClick={() => {
              setReplacing(false);
              setKey('');
            }}
            data-testid="claude-replace-cancel"
          >
            {ctx.tr('cancel')}
          </Button>
        )}
      </div>
      <a
        className="link-btn ai-key-get"
        href={KEY_CONSOLE_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="claude-key-get"
      >
        {ctx.tr('aiKeyGet')}
        <Icon name="arrowRight" size="sm" />
      </a>
    </>
  );
  return (
    <div className="ai-settings" data-testid="claude-card">
      {view.legacy && (
        <Card className="tab-card ai-legacy" data-testid="claude-legacy">
          <Banner icon="key">{ctx.tr('aiLegacy', { hint: view.legacy.last4 })}</Banner>
          <div className="btn-row">
            <Button
              size="sm"
              busy={view.saving}
              disabled={view.saving}
              onClick={() => void assistant.saveLegacy()}
              data-testid="claude-legacy-save"
            >
              {ctx.tr('aiLegacySave')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={view.saving}
              onClick={() => assistant.discardLegacy()}
              data-testid="claude-legacy-discard"
            >
              {ctx.tr('aiLegacyDiscard')}
            </Button>
          </div>
        </Card>
      )}
      {!view.loaded && !view.keyError ? (
        <Card className="tab-card">
          <Pending label={ctx.tr('loading')} />
        </Card>
      ) : !stored || replacing ? (
        <Card className="tab-card" data-testid="claude-key-card">
          <CardHeader title={ctx.tr('aiKeyTitle')} info={info(ctx.tr('aiKeyInfo'), 'claude-key-info')} />
          {entry}
        </Card>
      ) : (
        <Card className="tab-card" data-testid="claude-key-card">
          <div className="ai-key-saved" data-testid="claude-saved">
            <span className="ai-key-tile" aria-hidden="true">
              <Icon name="sparkle" />
            </span>
            <span className="row-text">
              <span className="row-label">{ctx.tr('aiKeySaved')}</span>
              <span className="hint num">
                {ctx.tr('aiKeySince', {
                  hint: `…⁠${stored.last4}`,
                  date: dateText(Math.floor(stored.setAt / 1000), ctx.lang),
                })}
              </span>
            </span>
          </div>
          <div className="btn-row ai-key-actions">
            <Button
              variant="secondary"
              size="sm"
              className="ai-small"
              onClick={() => setReplacing(true)}
              data-testid="claude-replace"
            >
              {ctx.tr('aiKeyReplace')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="ai-key-delete ai-small"
              onClick={() => setForgetting(true)}
              data-testid="claude-forget"
            >
              {ctx.tr('aiForget')}
            </Button>
          </div>
          {view.keyError && (
            <p className="field-error" role="alert">
              {failureText(ctx.lang, view.keyError)}
            </p>
          )}
        </Card>
      )}
      {view.loaded && account && (
        <>
          {/* the switch alone with its (i), no title over one row (board "Einstellungen: Claude") */}
          <Card className="tab-card rows-card" data-testid="claude-auto-card">
            <div className="row-toggle">
              <Toggle
                id={autoId}
                className="ai-auto"
                label={ctx.tr('aiAuto')}
                info={ctx.tr('aiAutoInfo')}
                infoLabel={ctx.tr('infoShow')}
                infoTestId="claude-auto-info"
                checked={account.autoAsk !== false}
                onChange={(e) => void assistant.setSettings({ autoAsk: e.target.checked })}
                data-testid="claude-auto"
              />
            </div>
          </Card>
          <Card className="tab-card">
            <CardHeader title={ctx.tr('aiModel')} info={info(ctx.tr('aiModelInfo'), 'claude-model-info')} />
            <Segmented<ClaudeModel>
              label={ctx.tr('aiModel')}
              options={MODELS.map((m) => ({ ...m, on: account.model === m.value }))}
              disabled={view.saving}
              onPick={(model) => void assistant.setSettings({ model })}
              testId="claude-model"
            />
          </Card>
          <Card className="tab-card">
            <CardHeader
              title={ctx.tr('aiEffort')}
              info={info(ctx.tr('aiEffortInfo'), 'claude-effort-info')}
            />
            <Segmented<ClaudeEffort>
              label={ctx.tr('aiEffort')}
              options={[
                { value: 'standard', label: ctx.tr('aiEffortStandard'), on: account.effort === 'standard' },
                { value: 'thorough', label: ctx.tr('aiEffortThorough'), on: account.effort === 'thorough' },
              ]}
              disabled={view.saving}
              onPick={(effort) => void assistant.setSettings({ effort })}
              testId="claude-effort"
            />
          </Card>
        </>
      )}
      <p className="hint ai-privacy">{ctx.tr('aiPrivacy')}</p>
      {forgetting && (
        <AskSheet
          ask={{
            title: ctx.tr('aiForgetTitle'),
            text: ctx.tr('aiForgetText'),
            ok: ctx.tr('aiForget'),
            danger: true,
          }}
          titleId={titleId}
          onNo={() => setForgetting(false)}
          onYes={() => {
            setForgetting(false);
            void assistant.deleteKey();
          }}
          testId="claude-forget"
        />
      )}
    </div>
  );
}
